import axios, { AxiosResponse } from 'axios';
import { getAuthToken } from '@redpanda/utils';
import { InteractionData, Role } from '@redpanda/types';

const API_URL = process.env.REACT_APP_CRM_BACKEND_URL || 'http://localhost:5000/api';
const IDENTITY_API_URL = process.env.REACT_APP_IDENTITY_SERVICE_URL || 'http://localhost:3000/api';
const SUBSCRIPTION_API_URL = process.env.REACT_APP_SUBSCRIPTION_SERVICE_URL || 'http://localhost:3001/api';

// 三個服務共用：帶 cookie，並自動加上 Authorization header
const createClient = (baseURL: string) => {
  const client = axios.create({ baseURL, withCredentials: true });
  client.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  return client;
};

const api = createClient(API_URL);
export const identityApi = createClient(IDENTITY_API_URL);
const subscriptionApi = createClient(SUBSCRIPTION_API_URL);

/**
 * 列表查詢參數（後端統一 page、pageSize，回應 { data, pagination }）
 * search 由後端搜尋全部資料（不是只過濾目前這一頁）
 */
export interface ListParams {
  page?: number;
  pageSize?: number;
  search?: string;
}

const listParams = (page: number, params: Omit<ListParams, 'page'> = {}) => ({ page, pageSize: 20, ...params });

// --- Project APIs ---
export const getProjects = (page = 1, params?: Omit<ListParams, 'page'>) => api.get('/projects', { params: listParams(page, params) });
export const createProject = (data: any) => api.post('/projects', data);
export const updateProject = (id: string, data: any) => api.patch(`/projects/${id}`, data);
export const deleteProject = (id: string) => api.delete(`/projects/${id}`);
export const bulkDeleteProjects = (ids: string[]) => api.delete('/projects/bulk', { data: { ids } });

// --- Quote APIs ---
export const getQuotes = (page = 1, params?: Omit<ListParams, 'page'>) => api.get('/quotes', { params: listParams(page, params) });
export const createQuote = (data: any) => api.post('/quotes', data);
export const updateQuote = (id: string, data: any) => api.patch(`/quotes/${id}`, data);
export const deleteQuote = (id: string) => api.delete(`/quotes/${id}`);
export const bulkDeleteQuotes = (ids: string[]) => api.delete('/quotes/bulk', { data: { ids } });

// --- Payment APIs ---
export const getPayments = (page = 1, params?: Omit<ListParams, 'page'>) => api.get('/payments', { params: listParams(page, params) });
export const createPayment = (data: any) => api.post('/payments', data);
export const updatePayment = (id: string, data: any) => api.patch(`/payments/${id}`, data);
export const deletePayment = (id: string) => api.delete(`/payments/${id}`);
export const bulkDeletePayments = (ids: string[]) => api.delete('/payments/bulk', { data: { ids } });

// --- Customer APIs ---
export interface CustomerListParams extends Omit<ListParams, 'page'> {
  archived?: boolean;
  tagIds?: string[];
  matchAll?: boolean;
}
export const getCustomers = (page = 1, { tagIds, ...params }: CustomerListParams = {}) => api.get('/customers', {
  params: listParams(page, { ...params, ...(tagIds && tagIds.length > 0 && { tagIds: tagIds.join(',') }) }),
});
export const getCustomerById = (id: string) => api.get(`/customers/${id}`);
export const createCustomer = (data: any) => api.post('/customers', data);
export const updateCustomer = (id: string, data: any) => api.patch(`/customers/${id}`, data);
export const deleteCustomer = (id: string) => api.delete(`/customers/${id}`);
export const bulkDeleteCustomers = (ids: string[]) => api.delete('/customers/bulk', { data: { ids } });
/** 批次封存（archived: true）／恢復（archived: false） */
export const setCustomersArchived = (ids: string[], archived: boolean) => api.patch('/customers/bulk', { ids, archived });

// --- Customer Limit APIs ---
export const getCustomerLimitInfo = () => subscriptionApi.get(`/subscriptions/customer-limit`);

// --- Interaction APIs ---
export const getInteractions = (customerId: string) => api.get(`/customers/${customerId}/interactions`);
export const createInteraction = (customerId: string, data: InteractionData) => api.post(`/customers/${customerId}/interactions`, data);
export const updateInteraction = (customerId: string, interactionId: string, data: InteractionData) => api.patch(`/customers/${customerId}/interactions/${interactionId}`, data);
export const deleteInteraction = (customerId: string, interactionId: string) => api.delete(`/customers/${customerId}/interactions/${interactionId}`);

// --- User Management APIs（identity-service；管理員面板 UserManagementTable 用）---
export const getApprovedUsers = () => identityApi.get('/admin/users/approved');
export const getPendingUsers = () => identityApi.get('/admin/users/pending');
export const updateUserRole = (userId: string, role: Role) =>
  identityApi.patch(`/admin/users/${userId}/role`, { role });
export const deleteUser = (userId: string) => identityApi.delete(`/admin/users/${userId}`);
export const approveUser = (userId: string, role: Role, config?: { allowedSystems?: string[] }) =>
  identityApi.post(`/admin/users/${userId}/approve`, { role, ...config });
export const rejectUser = (userId: string) => identityApi.post(`/admin/users/${userId}/reject`);

// --- Dashboard APIs ---
export const getDashboardStats = () => api.get('/dashboard/stats');

// --- Activity APIs ---
export const getActivities = (page = 1, pageSize = 20) => api.get('/activities', { params: { page, pageSize } });

// --- Report APIs ---
export const getRevenueReport = () => api.get('/reports/revenue');
export const getCustomerSourceReport = (includeArchived = false) =>
  api.get('/reports/customer-sources', { params: { includeArchived } });
export const getCustomerTagReport = (includeArchived = false) =>
  api.get('/reports/customer-tags', { params: { includeArchived } });
export const getCustomerTrendReport = (includeArchived = false) =>
  api.get('/reports/customer-trends', { params: { includeArchived } });

// --- Data I/O APIs（Excel 匯出／匯入）---
export const exportToExcel = (sheets: string[], language?: string) =>
  api.post('/data/exports', { sheets, language }, { responseType: 'blob' });

export const importFromExcel = (file: File, language?: string, onUploadProgress?: (progressEvent: any) => void) => {
  const formData = new FormData();
  formData.append('file', file);
  if (language) {
    formData.append('language', language);
  }
  return api.post('/data/imports', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
};

// --- Task APIs ---
export const getTasks = (page = 1, params?: Omit<ListParams, 'page'>) => api.get('/tasks', { params: listParams(page, params) });
export const createTask = (data: any) => api.post('/tasks', data);
export const updateTask = (id: string, data: any) => api.patch(`/tasks/${id}`, data);
export const updateTaskStatus = (id: string, status: string) => api.patch(`/tasks/${id}/status`, { status });
export const deleteTask = (id: string) => api.delete(`/tasks/${id}`);
export const bulkDeleteTasks = (ids: string[]) => api.delete('/tasks/bulk', { data: { ids } });
export const getTasksByCustomerId = (customerId: string) => api.get(`/tasks/customer/${customerId}`);
export const getUpcomingTasks = () => api.get('/tasks/upcoming');

// --- Tag APIs ---
/** 標籤管理頁（分頁 { data, pagination }） */
export const getTags = (page = 1, params?: Omit<ListParams, 'page'> & { category?: string }) =>
  api.get('/tags', { params: listParams(page, params) });
/** 全部標籤（陣列；標籤選擇器、客戶篩選用） */
export const getAllTags = () => api.get('/tags');
export const createTag = (data: {
  name: string;
  color?: string;
  category: string;
}) => api.post('/tags', data);
export const updateTag = (id: string, data: {
  name?: string;
  color?: string;
  category?: string;
}) => api.patch(`/tags/${id}`, data);
export const deleteTag = (id: string) => api.delete(`/tags/${id}`);
export const bulkDeleteTags = (ids: string[]) => api.delete('/tags/bulk', { data: { ids } });
/** 組織還沒有標籤時建立預設標籤 */
export const createDefaultTags = (language: string = 'zh') => api.post('/tags/defaults', { language });

// --- Opportunity Stage APIs ---
export const getStages = () => api.get('/opportunities/stages');
export const createStage = (data: {
  name: string;
  orderIndex: number;
  probability: number;
}) => api.post('/opportunities/stages', data);
export const updateStage = (id: string, data: {
  name?: string;
  orderIndex?: number;
  probability?: number;
}) => api.patch(`/opportunities/stages/${id}`, data);
export const deleteStage = (id: string) => api.delete(`/opportunities/stages/${id}`);
/** 組織還沒有階段時建立預設階段 */
export const createDefaultStages = (language: string = 'zh') => api.post('/opportunities/stages/defaults', { language });

// --- Opportunity APIs ---
export const getOpportunities = (page = 1, params?: Omit<ListParams, 'page'> & { stageId?: string; customerId?: string }) =>
  api.get('/opportunities', { params: listParams(page, params) });
export const getOpportunityById = (id: string) => api.get(`/opportunities/${id}`);
export const createOpportunity = (data: {
  customerId: string;
  title: string;
  description?: string;
  value: number;
  currentStageId: string;
}) => api.post('/opportunities', data);
export const updateOpportunity = (id: string, data: {
  customerId?: string;
  title?: string;
  description?: string;
  value?: number;
  currentStageId?: string;
}) => api.patch(`/opportunities/${id}`, data);
export const deleteOpportunity = (id: string) => api.delete(`/opportunities/${id}`);
export const bulkDeleteOpportunities = (ids: string[]) => api.delete('/opportunities/bulk', { data: { ids } });
export const clearOpportunityStageHistory = (id: string) => api.delete(`/opportunities/${id}/stage-history`);
export const getCustomerOpportunities = (customerId: string) => api.get(`/opportunities/customer/${customerId}`);
// 同時進行的請求共用一次（商機異動時列表頁的階段統計與銷售漏斗會同時重抓同一支 API）
let salesFunnelStatsRequest: Promise<AxiosResponse<any>> | null = null;
export const getSalesFunnelStats = () => {
  if (!salesFunnelStatsRequest) {
    salesFunnelStatsRequest = api.get('/opportunities/stats/funnel').finally(() => {
      salesFunnelStatsRequest = null;
    });
  }
  return salesFunnelStatsRequest;
};

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      // 檢查是否為 401 Unauthorized 或 403 Forbidden 錯誤
      if (status === 401 || status === 403) {
        // 檢查錯誤訊息是否指示帳號被刪除或狀態不正確
        if (data && typeof data.error === 'string' &&
           (data.error.includes('Account not found or deleted.') || data.error.includes('Account status for user'))) {
          console.warn('帳號已被刪除或停用，自動登出。');
          // 使用新的 SSO 登出流程
          window.location.href = '/login';
        } else if ((status === 401 && data && typeof data.error === 'string' && data.error.includes('jwt expired')) ||
                   (status === 403 && data && typeof data.error === 'string' && (data.error.includes('Token is outdated. Please log in again.') || data.error.includes('Token is outdated due to role change')))) {
          console.warn('Token 已過期或權限已變更，自動登出。');
          // 使用新的 SSO 登出流程
          window.location.href = '/login';
        } else if (status === 401) {
          // 處理其他 401 錯誤，統一重定向到登入頁面
          console.warn('未授權錯誤，自動登出。');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
